import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, CalendarCheck, ChevronDown, Copy, ExternalLink, MessageCircle, Plus, Save, Trash2 } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import I18nTripleFields, { I18nTriple } from '../../components/Admin/I18nTripleFields';
import { emptyI18nTriple } from '../../i18n/localizeContent';
import { buildWhatsappMessage, copyText } from './Agendamentos';

interface TimeWindow {
  start: string;
  end: string;
}

interface Availability {
  timezone: string;
  startDate: string;
  endDate: string;
  weekdays: number[];
  windows: TimeWindow[];
  durationMin: number;
  bufferMin: number;
  capacityPerSlot: number;
  minNoticeHours: number;
}

interface AvatarOption {
  _id: string;
  name: string;
}

interface SavedLink {
  publicTitle: string;
  publicUrl: string;
  expiresAt: string;
}

const TIMEZONES = [
  'Europe/Lisbon',
  'Atlantic/Madeira',
  'Atlantic/Azores',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Paris',
  'America/Sao_Paulo',
  'America/Fortaleza',
  'America/Manaus',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Africa/Luanda',
  'Africa/Maputo'
];

// Seg..Dom na tela; o valor salvo segue Date.getDay() (0 = domingo)
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const toYmd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const toDateTimeLocal = (iso: string) => {
  const d = new Date(iso);
  return `${toYmd(d)}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// Altura fixa: date/time/datetime/number/select nativos têm alturas diferentes no Safari sem isso
const inputClass = 'block w-full h-10 px-3 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

// Select sem a aparência nativa (no Safari ele fica mais baixo e cinza que os inputs), com seta própria
const SelectField: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ children, className = '', ...props }) => (
  <div className="relative">
    <select {...props} className={`${inputClass} appearance-none pr-9 ${className}`}>
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
  </div>
);

const defaultAvailability = (): Availability => {
  const today = new Date();
  return {
    timezone: 'Europe/Lisbon',
    startDate: toYmd(today),
    endDate: toYmd(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14)),
    weekdays: [1, 2, 3, 4, 5],
    windows: [
      { start: '09:00', end: '12:00' },
      { start: '14:00', end: '18:00' }
    ],
    durationMin: 30,
    bufferMin: 0,
    capacityPerSlot: 1,
    minNoticeHours: 12
  };
};

const AgendamentoForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n: i18next } = useTranslation();
  const notifications = useNotifications();
  const isEditing = Boolean(id);

  const [title, setTitle] = useState('');
  const [i18n, setI18n] = useState<I18nTriple>(emptyI18nTriple());
  const [lang, setLang] = useState<'pt' | 'en' | 'es'>('pt');
  const [avatar, setAvatar] = useState(searchParams.get('avatar') || '');
  const [avatars, setAvatars] = useState<AvatarOption[]>([]);
  const [availability, setAvailability] = useState<Availability>(defaultAvailability);
  const [expiresAt, setExpiresAt] = useState('');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [location, setLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<SavedLink | null>(null);
  const [preview, setPreview] = useState<{ total: number; slots: { startAt: string }[] } | null>(null);
  const [previewError, setPreviewError] = useState('');

  const weekdayShort = t('agendamentoAdmin.weekdayShort', { returnObjects: true }) as string[];

  const timezones = useMemo(() => {
    const list = new Set(TIMEZONES);
    list.add(availability.timezone);
    return Array.from(list);
  }, [availability.timezone]);

  useEffect(() => {
    axios
      .get('/avatars?limit=50')
      .then((res) => setAvatars(res.data.avatars || []))
      .catch(() => setAvatars([]));
  }, []);

  // Vindo de "Gerar link de agendamento" no Avatar: sugere o nome interno
  useEffect(() => {
    if (isEditing || !avatar || title) return;
    const match = avatars.find((a) => a._id === avatar);
    if (match) setTitle(`${match.name} — ${t('agendamentoAdmin.title')}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatars]);

  const fetchLink = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await axios.get(`/agendamento/links/${id}`);
      setTitle(data.title || '');
      setI18n({
        pt: { publicTitle: data.i18n?.pt?.publicTitle || data.publicTitle || '', description: data.i18n?.pt?.description || data.description || '' },
        en: { publicTitle: data.i18n?.en?.publicTitle || '', description: data.i18n?.en?.description || '' },
        es: { publicTitle: data.i18n?.es?.publicTitle || '', description: data.i18n?.es?.description || '' }
      });
      setLang(data.lang || 'pt');
      setAvatar(data.avatar?._id || '');
      setAvailability({ ...defaultAvailability(), ...data.availability });
      setExpiresAt(data.expiresAt ? toDateTimeLocal(data.expiresAt) : '');
      setMeetingUrl(data.meetingUrl || '');
      setLocation(data.location || '');
    } catch (error) {
      console.error('Erro ao carregar link de agendamento:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchLink();
  }, [fetchLink]);

  // Pré-visualização dos horários gerados (debounce para não chamar a cada tecla)
  useEffect(() => {
    const handle = setTimeout(async () => {
      try {
        setPreviewError('');
        const { data } = await axios.post('/agendamento/links/preview', {
          availability,
          expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
          linkId: id
        });
        setPreview(data);
      } catch (error: any) {
        setPreview(null);
        setPreviewError(error.response?.data?.message || '');
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [availability, expiresAt, id]);

  const patchAvailability = (patch: Partial<Availability>) => setAvailability((prev) => ({ ...prev, ...patch }));

  const toggleWeekday = (day: number) =>
    patchAvailability({
      weekdays: availability.weekdays.includes(day)
        ? availability.weekdays.filter((d) => d !== day)
        : [...availability.weekdays, day]
    });

  const patchWindow = (index: number, patch: Partial<TimeWindow>) =>
    patchAvailability({ windows: availability.windows.map((w, i) => (i === index ? { ...w, ...patch } : w)) });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (title.trim().length < 2 || !i18n.pt?.publicTitle?.trim()) {
      notifications.showWarning(t('agendamentoAdmin.validationTitle'), t('agendamentoAdmin.titleRequired'));
      return;
    }
    setSaving(true);
    const payload = {
      title: title.trim(),
      i18n,
      lang,
      avatar: avatar || null,
      availability,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      meetingUrl: meetingUrl.trim(),
      location: location.trim()
    };
    try {
      if (isEditing) {
        await axios.put(`/agendamento/links/${id}`, payload);
        notifications.showSuccess(t('agendamentoAdmin.saved'), '');
        navigate('/admin/agendamentos');
      } else {
        const { data } = await axios.post('/agendamento/links', payload);
        notifications.showSuccess(t('agendamentoAdmin.saved'), '');
        setSaved(data);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error: any) {
      const first = error.response?.data?.errors?.[0]?.msg;
      notifications.showError(t('agendamentoAdmin.error'), first || error.response?.data?.message || t('agendamentoAdmin.tryAgain'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async (text: string, msgKey: string) => {
    if (await copyText(text)) notifications.showSuccess(t('agendamentoAdmin.copied'), t(msgKey));
  };

  const previewFmt = new Intl.DateTimeFormat(i18next.language, {
    timeZone: availability.timezone,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });

  if (saved) {
    const message = buildWhatsappMessage(t, saved, i18next.language);
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-10 sm:px-6">
          <div className="bg-white rounded-lg shadow p-6 sm:p-8">
            <CalendarCheck className="w-12 h-12 text-green-500 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">{t('agendamentoAdmin.shareTitle')}</h1>
            <div className="mt-5 flex flex-col sm:flex-row gap-2">
              <input readOnly value={saved.publicUrl} className={`${inputClass} bg-gray-50 font-mono text-sm`} onFocus={(e) => e.target.select()} />
              <button
                type="button"
                onClick={() => handleCopy(saved.publicUrl, 'agendamentoAdmin.linkCopied')}
                className="btn-secondary inline-flex items-center justify-center gap-2 shrink-0"
              >
                <Copy className="w-4 h-4" />
                {t('agendamentoAdmin.copyLink')}
              </button>
            </div>
            <pre className="mt-5 whitespace-pre-wrap rounded-md bg-gray-50 border border-gray-200 p-4 text-sm text-gray-800 font-sans">{message}</pre>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => handleCopy(message, 'agendamentoAdmin.whatsappCopied')}
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                {t('agendamentoAdmin.copyWhatsapp')}
              </button>
              <a
                href={saved.publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                {saved.publicUrl.replace(/^https?:\/\//, '').split('/')[0]}
              </a>
              <Link to="/admin/agendamentos" className="btn-secondary inline-flex items-center justify-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                {t('agendamentoAdmin.title')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex items-center gap-4">
          <button type="button" onClick={() => navigate('/admin/agendamentos')} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {isEditing ? t('agendamentoAdmin.editLink') : t('agendamentoAdmin.newLink')}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('agendamentoAdmin.sectionBasics')}</h3>
              <div>
                <label className={labelClass}>{t('agendamentoAdmin.internalTitle')} *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('agendamentoAdmin.internalTitlePh') as string}
                  className={inputClass}
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t('agendamentoAdmin.avatar')}</label>
                  <SelectField value={avatar} onChange={(e) => setAvatar(e.target.value)} className={inputClass}>
                    <option value="">{t('agendamentoAdmin.noAvatar')}</option>
                    {avatars.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.name}
                      </option>
                    ))}
                  </SelectField>
                  <p className="text-xs text-gray-500 mt-1">{t('agendamentoAdmin.avatarHint')}</p>
                </div>
                <div>
                  <label className={labelClass}>{t('agendamentoAdmin.defaultLang')}</label>
                  <SelectField value={lang} onChange={(e) => setLang(e.target.value as typeof lang)} className={inputClass}>
                    <option value="pt">Português</option>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                  </SelectField>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('agendamentoAdmin.publicTexts')}</h3>
              <I18nTripleFields mode="agendamento" value={i18n} onChange={setI18n} />
            </div>

            <div className="bg-white rounded-lg shadow p-6 space-y-5">
              <h3 className="text-lg font-semibold text-gray-900">{t('agendamentoAdmin.sectionAvailability')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>{t('agendamentoAdmin.startDate')}</label>
                  <input
                    type="date"
                    value={availability.startDate}
                    onChange={(e) => patchAvailability({ startDate: e.target.value })}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('agendamentoAdmin.endDate')}</label>
                  <input
                    type="date"
                    value={availability.endDate}
                    min={availability.startDate}
                    onChange={(e) => patchAvailability({ endDate: e.target.value })}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('agendamentoAdmin.timezone')}</label>
                  <SelectField value={availability.timezone} onChange={(e) => patchAvailability({ timezone: e.target.value })} className={inputClass}>
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </SelectField>
                </div>
              </div>

              <div>
                <span className={labelClass}>{t('agendamentoAdmin.weekdays')}</span>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAY_ORDER.map((day) => {
                    const on = availability.weekdays.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleWeekday(day)}
                        aria-pressed={on}
                        className={`min-w-[3.25rem] px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                          on ? 'bg-primary-600 border-primary-600 text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {weekdayShort[day]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <span className={labelClass}>{t('agendamentoAdmin.windows')}</span>
                <div className="space-y-2">
                  {availability.windows.map((w, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 w-8 shrink-0">{t('agendamentoAdmin.from')}</span>
                      <input type="time" value={w.start} onChange={(e) => patchWindow(index, { start: e.target.value })} className={`${inputClass} !w-32`} />
                      <span className="text-sm text-gray-500 w-8 shrink-0 text-center">{t('agendamentoAdmin.to')}</span>
                      <input type="time" value={w.end} onChange={(e) => patchWindow(index, { end: e.target.value })} className={`${inputClass} !w-32`} />
                      {availability.windows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => patchAvailability({ windows: availability.windows.filter((_, i) => i !== index) })}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title={t('agendamentoAdmin.removeWindow') as string}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => patchAvailability({ windows: [...availability.windows, { start: '14:00', end: '18:00' }] })}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline"
                >
                  <Plus className="w-4 h-4" />
                  {t('agendamentoAdmin.addWindow')}
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
                {(
                  [
                    ['durationMin', 5, 480, 5],
                    ['bufferMin', 0, 240, 5],
                    ['capacityPerSlot', 1, 500, 1],
                    ['minNoticeHours', 0, 720, 1]
                  ] as [keyof Availability, number, number, number][]
                ).map(([field, min, max, step]) => (
                  <div key={field}>
                    <label className={labelClass}>{t(`agendamentoAdmin.${field}`)}</label>
                    <input
                      type="number"
                      min={min}
                      max={max}
                      step={step}
                      value={availability[field] as number}
                      onChange={(e) => patchAvailability({ [field]: Number(e.target.value) } as Partial<Availability>)}
                      className={inputClass}
                    />
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>{t('agendamentoAdmin.expiresAt')}</label>
                  <input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className={inputClass} />
                </div>
                <p className="sm:col-span-2 sm:self-end sm:pb-2.5 text-xs text-gray-500">{t('agendamentoAdmin.expiresAtHint')}</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('agendamentoAdmin.sectionMeeting')}</h3>
              <div>
                <label className={labelClass}>{t('agendamentoAdmin.meetingUrl')}</label>
                <input
                  type="url"
                  value={meetingUrl}
                  onChange={(e) => setMeetingUrl(e.target.value)}
                  placeholder="https://meet.google.com/..."
                  className={inputClass}
                />
                <p className="text-xs text-gray-500 mt-1">{t('agendamentoAdmin.meetingUrlHint')}</p>
              </div>
              <div>
                <label className={labelClass}>{t('agendamentoAdmin.location')}</label>
                <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} className={inputClass} />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6 lg:sticky lg:top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('agendamentoAdmin.preview')}</h3>
              {previewError ? (
                <p className="text-sm text-red-600">{previewError}</p>
              ) : preview ? (
                preview.total === 0 ? (
                  <p className="text-sm text-gray-500">{t('agendamentoAdmin.previewEmpty')}</p>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 mb-3">{t('agendamentoAdmin.previewCount', { count: preview.total })}</p>
                    <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100 text-sm">
                      {preview.slots.map((s) => (
                        <li key={s.startAt} className="py-1.5 text-gray-800 first-letter:uppercase">
                          {previewFmt.format(new Date(s.startAt))}
                        </li>
                      ))}
                    </ul>
                    {preview.total > preview.slots.length && (
                      <p className="text-xs text-gray-500 mt-2">{t('agendamentoAdmin.previewMore', { count: preview.total - preview.slots.length })}</p>
                    )}
                  </>
                )
              ) : (
                <p className="text-sm text-gray-400">{t('common.loading')}</p>
              )}

              <button type="submit" disabled={saving} className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2">
                <Save className="w-4 h-4" />
                {saving ? t('common.loading') : t('agendamentoAdmin.save')}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AgendamentoForm;
